import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { useGetProjectDetails } from "../../../../../queries/query-hooks/workspace/workspace-hook";
import {
  useGetBrowserInfo,
  useGetContentInfo,
  useGetContentReach,
  useGetContentView,
  useGetContentViewsAndReach,
  useGetDeviceInfo,
  useGetLocationInfo,
  useGetMobileDeviceInfo,
  useGetSpecialityInfo,
  useGetZoneInfo,
} from "../../../../../queries/query-hooks/workspace/sponsored-community-hook";
import {
  transformGraphData,
  transformHeatMapData,
} from "../../../../../common/utils/utils";
import {
  getContentIcon,
  transformIndiaMapData,
  transformSpecialityData,
} from "../utils/commonFunctions";
import BasicColumnChart from "../../../../../components/chart/BasicColumnChart";
import BarChartSkeleton from "../../../../../skeleton/workspace/clirnet/brand-ads/bar-chart-skeleton";
import DashboardDropdown from "../../../../../components/DashboardDropdown";
import IndiaMap from "../../../../../components/chart/IndiaMap";
import SquareChartSkeleton from "../../../../../skeleton/workspace/clirnet/brand-ads/square-chart-skeleton";
import MixedLineChart from "../../../../../components/chart/MixedLineChart";
import TreeMapChart from "../../../../../components/chart/TreeMapChart";
import DonutChart from "../../../../../components/chart/DonutChart";
import PolarChart from "../../../../../components/chart/PolarChart";
import { Col, Row } from "react-bootstrap";
import Skeleton from "../../../../../components/skeleton";
import { toast } from "react-hot-toast";
import { fallbackImages } from "../../../../../common/constants/fallback-images";
import { ProjectHeaderTopBar, CardNotFound } from "@ui";

const ContentAnalytics = () => {
  const { project_id, content_type, content_id } = useParams();
  const { data: projectDetails, isLoading: isProjectDetailsLoading } =
    useGetProjectDetails(project_id, true);
  const { data: contentView, isLoading: isContentViewLoading } =
    useGetContentView(
      project_id,
      {
        type: "content",
        content_type: content_type,
        content_id: content_id,
      },
      !!project_id && !!content_type && !!content_id
    );
  const [filterBy, setFilterBy] = useState("Views");
  const { data: contentReach, isLoading: isContentReachLoading } =
    useGetContentReach(
      project_id,
      {
        type: "content",
        content_type: content_type,
        content_id: content_id,
      },
      !!project_id && !!content_type && !!content_id
    );

  const contentViewChartOptions = {
    series: [
      {
        name: "View Count",
        data:
          contentView?.length > 0
            ? transformGraphData(contentView, "total_count", "formatted_date")
                ?.series
            : [],
        color: "#3256B8",
      },
    ],
    label:
      contentView?.length > 0
        ? transformGraphData(contentView, "total_count", "formatted_date")
            ?.label
        : [],
  };

  const contentReachChartOptions = {
    series: [
      {
        name: "Reach Count",
        data:
          contentReach?.length > 0
            ? transformGraphData(contentReach, "reach_count", "formatted_date")
                ?.series
            : [],
        color: "#159A6C",
      },
    ],
    label:
      contentReach?.length > 0
        ? transformGraphData(contentReach, "reach_count", "formatted_date")
            ?.label
        : [],
  };

  const { data: locationInfo, isLoading: isLocationInfoLoading } =
    useGetLocationInfo(
      project_id,
      {
        type: filterBy?.toLowerCase(),
        content_id: content_id,
        content_type: content_type,
        is_content: true,
      },
      !!project_id && !!content_type && !!content_id
    );
  const { data: zoneInfo, isLoading: isZoneInfoLoading } = useGetZoneInfo(
    project_id,
    {
      type: filterBy?.toLowerCase(),
      content_id: content_id,
      content_type: content_type,
      is_content: true,
    },
    !!project_id && !!content_type && !!content_id
  );

  const { data: deviceInfo, isLoading: isDeviceInfoLoading } = useGetDeviceInfo(
    project_id,
    {
      type: filterBy?.toLowerCase(),
      content_id: content_id,
      content_type: content_type,
      is_content: true,
    },
    !!project_id && !!content_type && !!content_id
  );

  const { data: mobileDeviceInfo, isLoading: isMobileDeviceInfoLoading } =
    useGetMobileDeviceInfo(
      project_id,
      {
        type: filterBy?.toLowerCase(),
        content_id: content_id,
        content_type: content_type,
        is_content: true,
      },
      !!project_id && !!content_type && !!content_id
    );

  const { data: browserInfo, isLoading: isBrowserInfoLoading } =
    useGetBrowserInfo(
      project_id,
      {
        type: filterBy?.toLowerCase(),
        content_id: content_id,
        content_type: content_type,
        is_content: true,
      },
      !!project_id && !!content_type && !!content_id
    );
  const { data: contentCount, isLoading: isContentCountLoading } =
    useGetContentViewsAndReach(
      project_id,
      { type: filterBy?.toLowerCase(), content_id: content_id },
      !!project_id && !!content_id
    );
  const { data: specialityInfo, isLoading: isSpecialityInfoLoading } =
    useGetSpecialityInfo(
      project_id,
      {
        type: filterBy?.toLowerCase(),
        content_id: content_id,
        content_type: content_type,
        is_content: true,
      },
      !!project_id && !!content_type && !!content_id
    );
  const { data: contentInfo, isLoading: isContentInfoLoading } =
    useGetContentInfo(
      project_id,
      {
        from: 0,
        to: 10,
        filter: [
          {
            type: content_type,
            query: "",
            start_date: "",
            end_date: "",
            content_id: content_id,
            limit: 1,
            offset: 0,
          },
        ],
      },
      !!project_id && !!content_id && !!content_type
    );

  const formattedSpecialityInfo = transformSpecialityData(specialityInfo);

  const indiaMapData = transformIndiaMapData(locationInfo);

  const zoneInfoChartOptions =
    zoneInfo && zoneInfo?.length > 0
      ? transformHeatMapData(
          zoneInfo,
          "name",
          filterBy === "Reads" ? "read_count" : "view_count"
        )
      : [];

  const deviceChartOptions = {
    series:
      deviceInfo?.length > 0
        ? transformGraphData(deviceInfo, "percentagebrowser_count", "Device")
            ?.series
        : [],
    label:
      deviceInfo?.length > 0
        ? transformGraphData(deviceInfo, "percentagebrowser_count", "Device")
            ?.label
        : [],
    title: "Device Distribution",
  };

  const mobileDeviceChartOptions = {
    series:
      mobileDeviceInfo?.length > 0
        ? transformGraphData(
            mobileDeviceInfo,
            "percentagebrowser_count",
            "Device"
          )?.series
        : [],
    label:
      mobileDeviceInfo?.length > 0
        ? transformGraphData(
            mobileDeviceInfo,
            "percentagebrowser_count",
            "Device"
          )?.label
        : [],
    title: "Mobile Device Distribution",
  };

  const browserChartOptions = {
    series:
      browserInfo?.length > 0
        ? transformGraphData(
            browserInfo,
            "percentagebrowser_count",
            "new_browser"
          )?.series
        : [],
    label:
      browserInfo?.length > 0
        ? transformGraphData(
            browserInfo,
            "percentagebrowser_count",
            "new_browser"
          )?.label
        : [],
  };

  const updateFilterHandler = (value) => {
    setFilterBy(value);
  };

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: contentInfo?.rows?.at(0)?.title,
          text: contentInfo?.rows?.at(0)?.title,
          url: contentInfo?.rows?.at(0)?.deeplink,
        });
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(contentInfo?.rows?.at(0)?.deeplink);
        toast.success(`The link has been copied to the clipboard`);
      } else {
        window.open(
          `mailto:?subject=${encodeURIComponent(
            contentInfo?.rows?.at(0)?.title
          )}&body=${encodeURIComponent(
            contentInfo?.rows?.at(0)?.title +
              "\n" +
              contentInfo?.rows?.at(0)?.deeplink
          )}`
        );
      }
    } catch (error) {
      console.error("Error sharing:", error);
    }
  };

  const showSpecialityInfo =
    projectDetails
      ?.at(0)
      ?.project_attributes?.find(
        (item) => item?.attribute_name === "show_speciality_info"
      )?.attribute_value === "0"
      ? false
      : true;

  return (
    <div>
      <div className="mb-3">
        <ProjectHeaderTopBar
          projectType="CLIRNET Channel"
          title="Content Analytics"
          projectStartDate={projectDetails?.at(0)?.project_start_date || ""}
          projectEndDate={projectDetails?.at(0)?.project_end_date || ""}
          status={projectDetails?.at(0)?.project_status}
          isProjectDetailsLoading={isProjectDetailsLoading}
        />
      </div>

      {!isContentInfoLoading ? (
        <div className="bg-white p-3 rounded-3 d-flex align-items-center justify-content-between mb-3">
          <div className="col-md-8 d-flex gap-3 align-items-center">
            <div>
              <img
                src={contentInfo?.rows?.at(0)?.thumbnail}
                onError={(e) => (e.target.src = fallbackImages.noImage)}
                alt={"content-image"}
                className="rounded-3 object-fit-contain"
                width={150}
                height={80}
              />
            </div>
            <div>
              <div className="d-flex gap-2 mb-2">
                <div className="bg-light rounded-pill badge text-primary d-flex gap-1 bg-opacity-25 d-flex align-items-end">
                  <span>{getContentIcon(contentInfo?.rows?.at(0)?.type)}</span>
                  <span className="fs-7">{contentInfo?.rows?.at(0)?.type}</span>
                </div>
                {contentInfo?.rows?.at(0)?.specialities?.length > 0 &&
                  contentInfo?.rows
                    ?.at(0)
                    ?.specialities?.map((speciality, index) => (
                      <div
                        key={`speciality-${index}`}
                        className="bg-light rounded-pill badge text-primary d-flex gap-1 bg-opacity-25 d-flex align-items-end"
                      >
                        <span key={`speciality-${index}`} className="fs-7 me-1">
                          {speciality}
                        </span>
                      </div>
                    ))}
              </div>
              <p className="poppins-medium fs-6 m-0 two-line-clamp text-black mb-2 lh-sm">
                {contentInfo?.rows?.at(0)?.title}
              </p>
            </div>
          </div>
          <div className="col-md-4 d-flex justify-content-md-end justify-content-start mt-3 mt-md-0">
            <button
              className="btn btn-primary rounded-3 me-3"
              onClick={() =>
                window.open(contentInfo?.rows?.at(0)?.deeplink, "_blank")
              }
            >
              View Content
            </button>
            <button className="btn btn-light rounded-3" onClick={handleShare}>
              Share
            </button>
          </div>
        </div>
      ) : (
        <Skeleton variant="text" width={"100%"} height={100} />
      )}
      <div className="mb-3 mb-md-4 row">
        <div className="col-md-6">
          {!isContentViewLoading && contentView?.length > 0 ? (
            <BasicColumnChart
              title={"Content Views"}
              series={contentViewChartOptions.series}
              labels={contentViewChartOptions.label}
            />
          ) : isContentViewLoading ? (
            <BarChartSkeleton />
          ) : (
            <CardNotFound title={"Content Views"} height={320} />
          )}
        </div>
        <div className="col-md-6">
          {!isContentReachLoading && contentReach?.length > 0 ? (
            <BasicColumnChart
              title={"Content Reach"}
              series={contentReachChartOptions.series}
              labels={contentReachChartOptions.label}
            />
          ) : isContentReachLoading ? (
            <BarChartSkeleton />
          ) : (
            <CardNotFound title={"Content Reach"} height={320} />
          )}
        </div>
      </div>

      <Row>
        <Col sm={12} className="mb-3">
          <div className="d-flex gap-3 align-items-center justify-content-end">
            <div className="fs-6 text-black">Filter By</div>
            <div className="dropdown bg-white border border-1 rounded-3 px-3 py-2">
              <DashboardDropdown
                constantFilterOption={["Views", "Reads"]}
                handleDropdownChange={updateFilterHandler}
                type={filterBy}
              />
            </div>
          </div>
        </Col>
      </Row>

      <Row>
        <Col
          md={12}
          lg={showSpecialityInfo ? 6 : 4}
          sm={12}
          className="d-flex flex-column pe-2 mb-4"
        >
          {!isLocationInfoLoading && locationInfo?.length > 0 ? (
            <IndiaMap
              stateData={indiaMapData}
              title={`${filterBy} by Location`}
              showStateLegend={false}
              valueSuffix={"%"}
            />
          ) : isLocationInfoLoading ? (
            <SquareChartSkeleton height={"100%"} />
          ) : (
            <CardNotFound title={`${filterBy} by Location`} />
          )}
        </Col>

        <Col md={12} lg={showSpecialityInfo ? 6 : 8} className="d-flex">
          <Row className="flex-grow-1">
            {showSpecialityInfo && (
              <Col md={12} sm={12} className="mb-4">
                {!isSpecialityInfoLoading && specialityInfo?.length > 0 ? (
                  <MixedLineChart
                    chartTitle={`${filterBy} by Speciality`}
                    series={formattedSpecialityInfo.series}
                    labels={formattedSpecialityInfo.labels}
                  />
                ) : isSpecialityInfoLoading ? (
                  <SquareChartSkeleton height={320} />
                ) : (
                  <CardNotFound
                    title={`${filterBy} by Speciality`}
                    height={320}
                  />
                )}
              </Col>
            )}
            <Col md={12} sm={12} className="mb-4">
              {!isZoneInfoLoading && zoneInfo?.length > 0 ? (
                <TreeMapChart
                  title={`${filterBy} by Zone`}
                  series={zoneInfoChartOptions}
                />
              ) : isZoneInfoLoading ? (
                <SquareChartSkeleton height={320} />
              ) : (
                <CardNotFound title={`${filterBy} by Zone`} height={320} />
              )}
            </Col>
          </Row>
        </Col>

        <Col sm={12} className="mb-4">
          {!isContentCountLoading ? (
            <div
              className="bg-primary-light border border-1 border-primary border-opacity-25 rounded-3 text-center text-primary mb-0 py-3 fs-5 poppins-medium"
              role="alert"
            >
              The community has has received
              <span> {contentCount?.at(0)?.count} </span>
              {filterBy?.toLowerCase()} since last month
            </div>
          ) : (
            <Skeleton variant="text" width={"100%"} height={100} />
          )}
        </Col>

        <Col md={4} sm={12} className="mb-4">
          {!isDeviceInfoLoading && deviceInfo?.length > 0 ? (
            <DonutChart
              series={deviceChartOptions.series}
              labels={deviceChartOptions.label}
              title={`${filterBy} by Device`}
              graphColor={["#13388C", "#159A6C", "#CECDCD"]}
              height={320}
            />
          ) : isDeviceInfoLoading ? (
            <SquareChartSkeleton height={320} />
          ) : (
            <CardNotFound title={`${filterBy} by Device`} height={320} />
          )}
        </Col>

        <Col md={4} sm={12} className="mb-4">
          {!isMobileDeviceInfoLoading && mobileDeviceInfo?.length > 0 ? (
            <DonutChart
              series={mobileDeviceChartOptions.series}
              labels={mobileDeviceChartOptions.label}
              title={`${filterBy} by Mobile Device`}
              graphColor={["#13388C", "#159A6C", "#CECDCD"]}
              height={320}
            />
          ) : isMobileDeviceInfoLoading ? (
            <SquareChartSkeleton height={320} />
          ) : (
            <CardNotFound title={`${filterBy} by Mobile Device`} height={320} />
          )}
        </Col>

        <Col md={4} sm={12} className="mb-4">
          {!isBrowserInfoLoading && browserInfo?.length > 0 ? (
            <PolarChart
              series={browserChartOptions.series}
              labels={browserChartOptions.label}
              title={`${filterBy} by Browser`}
              graphColor={[
                "#3256B8",
                "#159A6C",
                "#15607A",
                "#1D81A2",
                "#CECDCD",
              ]}
              height={320}
            />
          ) : isBrowserInfoLoading ? (
            <SquareChartSkeleton height={320} />
          ) : (
            <CardNotFound title={`${filterBy} by Browser`} height={320} />
          )}
        </Col>
      </Row>
    </div>
  );
};

export default ContentAnalytics;
